import React, { useState, useEffect, useContext } from "react";
import { useSearchParams } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { Box, CircularProgress, Typography, Divider } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import PrimaryTextField from "../../Inputs/PrimaryTextField/PrimaryTextField";
import PrimarySelect from "../../Inputs/PrimarySelect/PrimarySelect";
import PrimaryButton from "../../Buttons/PrimaryButton/PrimaryButton";
import SecondaryButton from "../../Buttons/SecondaryButton/SecondaryButton";
import LoadStatusPill from "../../Pills/LoadStatusPill/LoadStatusPill";
import { handleAltEnter } from "../../../utils/keyboardShortcutHelpers";
import { formatTitleCase } from "../../../utils/formatHelpers";
import { CarrierService } from "../../../services/CarrierService";
import { LoadService } from "../../../services/LoadService";
import { AlertContext } from "../../Dashboard/Dashboard";

export default function BounceModal() {
  const { instance } = useMsal();
  const { warning } = useContext(AlertContext);
  let [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [load, setLoad] = useState(null);
  const [originLocation, setOriginLocation] = useState("");
  const [destinationLocation, setDestinationLocation] = useState("");
  const [reasons, setReasons] = useState([]);
  const [selectedReason, setSelectedReason] = useState("");
  const [comment, setComment] = useState("");

  const handleClose = () => {
    window.close();
  };

  const handleCancel = () => {
    handleClose();
  };

  const handleSubmit = () => {
     const loadId = searchParams.get("loadId");
    let payload = {
      movementId: load.movementId,
      reasonCode: selectedReason,
      comment: comment,
      orderId: loadId
    };

    setIsLoading(true);
    CarrierService.RemoveCarrierLoad(instance, payload).then(() => {
      setIsLoading(false);
      handleClose();
    }).catch(error => {
      console.error(error);
      setIsLoading(false);
      warning(error);
    });
  };

  useEffect(() => {
    if (load?.stops) {
      const originStop = load.stops[0];
      const destinationStop = load.stops[load.stops.length - 1];

      setOriginLocation(`${formatTitleCase(originStop.cityName)}, ${originStop.state}`);
      setDestinationLocation(`${formatTitleCase(destinationStop.cityName)}, ${destinationStop.state}`);
    }
  }, [load]);

  useEffect(() => {
    const loadId = searchParams.get("loadId");
    if (loadId) {
      setIsLoading(true);
      LoadService.GetLoad(instance, loadId).then(res => {
        setLoad(res);
        setIsLoading(false);
      }).catch(error => {
        console.error(error);
        setIsLoading(false);
        warning(error);
      });
    }

    CarrierService.GetReasonsCode(instance).then(res => {
      setReasons(res.reasons);
    }).catch(error => {
      console.error(error);
      warning(error);
    });
  }, []);

  return (
    <Box onKeyDown={(e) => handleAltEnter(e, !selectedReason || !comment, handleSubmit)} sx={{ margin: "10px" }}>
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
          <CircularProgress sx={{ color: "active.main" }} size={40} />
        </Box>
      ) : (
        <Box>
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", marginBottom: "5px" }}>
              <Typography variant="h2" color="black_opacity.100">
                {load?.id}
              </Typography>
              <Typography variant="h4" color="black_opacity.80" sx={{ marginLeft: "20px" }}>
                {originLocation}
              </Typography>
              <ArrowForwardIcon sx={{ marginLeft: "20px", color: "black_opacity.80", fontSize: "20px" }} />
              <Typography variant="h4" color="black_opacity.80" sx={{ marginLeft: "20px" }}>
                {destinationLocation}
              </Typography>
              <Box sx={{ marginLeft: "20px" }}>
                <LoadStatusPill value={load?.brokerageStatus} darkTheme />
              </Box>
            </Box>
            <Divider />
          </Box>
          <Box sx={{ display: "flex" }} >
            <Box sx={{ flexGrow: 1 }}>
              <Box sx={{ display: "flex", marginY: "20px" }}>
                <PrimarySelect
                  label="Reason *"
                  value={selectedReason}
                  onChange={setSelectedReason}
                  options={reasons.map((o) => (o))}
                  flexGrow={1}
                  nameKey="descr"
                  valueKey="id"
                  error={!selectedReason}
                />
              </Box>
              <Box sx={{ marginY: "20px" }}>
                <PrimaryTextField
                  label="Comments *"
                  value={comment}
                  onChange={setComment}
                  placeholder="Comments"
                  multiline
                  fullWidth
                  rows={9}
                  error={!comment}
                />
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: "flex", marginTop: "9px", alignItems: "center" }}>
            <Box sx={{ margin: "0 20px 0 auto" }}>
              <SecondaryButton
                label="Cancel"
                onClick={handleCancel}
              />
            </Box>
            <PrimaryButton
              label="Submit"
              onClick={handleSubmit}
              isLoading={isLoading}
              disabled={!selectedReason || !comment}
            />
          </Box>
        </Box>
      )
      }
    </Box >
  );
}

BounceModal.propTypes = {};